/**
 * Release QA Types
 *
 * Frontend types for Release QA runs
 */

export type ReleaseQARunStatus =
  | 'pending'
  | 'cloning'
  | 'analyzing'
  | 'planning'
  | 'building_workflows'
  | 'testing'
  | 'compiling_report'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'canceled'
  | 'timeout';

export type ReleaseQATriggerSource = 'manual' | 'scheduled' | 'webhook' | 'api';

export type ReleaseQACutoffReason =
  | 'max_agents'
  | 'timeout'
  | 'user_canceled'
  | 'all_tests_complete'
  | 'planning_failed';

export type ReleaseQATesterStatus = 'pending' | 'queued' | 'running' | 'completed' | 'failed';

export type ReleaseQATodoStatus = 'pending' | 'healthy' | 'unhealthy' | 'not_applicable';

export type ReleaseQATodoItem = {
  id: string;
  item: string;
  status: ReleaseQATodoStatus;
  summary?: string | null;
};

/** List item for releases table */
export type ReleaseRunListItem = {
  id: string;
  orgId: string;
  propertyId: string;
  propertyName: string;
  title: string | null;
  status: ReleaseQARunStatus;
  triggerSource: ReleaseQATriggerSource;
  fromSha: string | null;
  toSha: string | null;
  commitsAnalyzed: number;
  testersSpawned: number;
  testersCompleted: number;
  testersFailed: number;
  issuesFound: number;
  regressionTestsTotal: number;
  regressionTestsPassed: number;
  regressionTestsFailed: number;
  workflowBuildsRequested: number;
  workflowBuildsCompleted: number;
  workflowBuildsFailed: number;
  cutoffReason: ReleaseQACutoffReason | null;
  startedAt: string | null;
  planningCompletedAt: string | null;
  testingCompletedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

export type ReleaseRegressionConfig = {
  selectionMode: 'all_published' | 'tag';
  tags: string[];
  environmentId: string | null;
};

/** Suite linked to a release run (release agent — slice 4/5). */
export type ReleaseQARunSuiteSummary = {
  suiteId: string;
  name: string;
  executionId: string | null;
};

/**
 * Suite linked to a release QA config (release agent — slice 4/5).
 * Distinct shape from `ReleaseQARunSuiteSummary`: a config-level link has no
 * `executionId` (that's a run-time concept) and instead carries `linkId`,
 * the junction row id, keyed by `testSuiteId` rather than `suiteId`.
 * Mirrors `ReleaseQAConfigSuiteEntry` in
 * `packages/db/src/repositories/release-qa-configs.ts`.
 */
export type ReleaseQAConfigSuiteEntry = {
  linkId: string;
  testSuiteId: string;
  name: string;
};

/** Full release run detail */
export type ReleaseRunDetail = ReleaseRunListItem & {
  jobId: string | null;
  configId: string | null;
  workflowTestExecutionId: string | null;
  credentials: Array<{ credentialId: string; credentialName: string | null }>;
  releaseNotesMarkdown: string | null;
  releaseNotesCompletedAt: string | null;
  repoFullName: string | null;
  regressionConfig?: ReleaseRegressionConfig | null;
  /**
   * Per-repo SHA snapshot, written when the run is created and refined when
   * the run starts; `fromSha` is null when the repo has no earlier release.
   * Multi-repo runs populate this with one entry per participating repo.
   * Single-repo and pre-multi-repo runs may have an empty array — consumers
   * should fall back to the top-level `repoFullName` / `fromSha` / `toSha`.
   */
  repoCheckpoints: Array<{
    propertyGithubRepoId: string;
    repoFullName: string;
    fromSha: string | null;
    toSha: string;
  }>;
  /** Release agent (specs/pending/release-agent.md slice 4). */
  agentVerdict: 'shipped' | 'held' | 'failed' | null;
  agentVerdictMd: string | null;
  /** Presence means this run has a release agent — non-null enables the agent-status fetch behind the assistant auto-open. */
  durableAgentId: string | null;
  /** Suites linked to this run's release agent. Empty for non-agent runs. */
  suites: ReleaseQARunSuiteSummary[];
};

/** Tester item */
export type ReleaseTesterItem = {
  id: string;
  releaseRunId: string;
  testAreaId: string | null;
  testAreaName: string | null;
  credentialId: string | null;
  credentialName: string | null;
  status: ReleaseQATesterStatus;
  todoItems: ReleaseQATodoItem[] | null;
  issuesFound: number;
  summary: string | null;
  healthStatus: 'healthy' | 'unhealthy' | 'mixed' | null;
  videoStorageKey: string | null;
  traceStorageKey: string | null;
  screenshotStorageKey: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

export type ReleaseRegressionStatus = 'pending' | 'passed' | 'failed' | 'skipped';

/** Regression result item */
export type ReleaseRegressionItem = {
  id: string;
  releaseRunId: string;
  workflowId: string;
  workflowName: string;
  workflowTestRunId: string | null;
  status: ReleaseRegressionStatus;
  errorMessage: string | null;
  durationMs: number | null;
  videoStorageKey: string | null;
  traceStorageKey: string | null;
  createdAt: string;
};

// API Response types
export type ReleaseRunsResponse = {
  ok: boolean;
  runs: ReleaseRunListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

export type ReleaseRunDetailResponse = {
  ok: boolean;
  run: ReleaseRunDetail;
};

export type StartReleaseRunResponse = {
  ok: boolean;
  run: {
    releaseRunId: string;
    jobId: string;
  };
};

export type ReleaseTestersResponse = {
  ok: boolean;
  testers: ReleaseTesterItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

/** Live counts over the full (unpaginated, unfiltered) regression result set. */
export type ReleaseRegressionSummary = {
  total: number;
  pending: number;
  passed: number;
  failed: number;
  skipped: number;
};

export type ReleaseRegressionsResponse = {
  ok: boolean;
  regressions: ReleaseRegressionItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  summary: ReleaseRegressionSummary;
};

// Helper functions
export function getReleaseRunStatusMeta(status: ReleaseQARunStatus): {
  label: string;
  color: 'green' | 'blue' | 'yellow' | 'red' | 'gray';
} {
  switch (status) {
    case 'pending':
      return { label: 'Pending', color: 'gray' };
    case 'cloning':
      return { label: 'Cloning', color: 'blue' };
    case 'analyzing':
      return { label: 'Analyzing', color: 'blue' };
    case 'planning':
      return { label: 'Planning', color: 'blue' };
    case 'building_workflows':
      return { label: 'Building', color: 'blue' };
    case 'testing':
      return { label: 'Testing', color: 'blue' };
    case 'compiling_report':
      return { label: 'Compiling', color: 'blue' };
    case 'completed':
      return { label: 'Completed', color: 'green' };
    case 'completed_with_errors':
      return { label: 'Errors', color: 'red' };
    case 'failed':
      return { label: 'Failed', color: 'red' };
    case 'canceled':
      return { label: 'Canceled', color: 'yellow' };
    case 'timeout':
      return { label: 'Timed Out', color: 'yellow' };
    default:
      return { label: 'Unknown', color: 'gray' };
  }
}

export function getReleaseTesterStatusMeta(status: ReleaseQATesterStatus): {
  label: string;
  color: 'green' | 'blue' | 'yellow' | 'red' | 'gray';
} {
  switch (status) {
    case 'pending':
      return { label: 'Pending', color: 'gray' };
    case 'queued':
      return { label: 'Queued', color: 'gray' };
    case 'running':
      return { label: 'Running', color: 'blue' };
    case 'completed':
      return { label: 'Completed', color: 'green' };
    case 'failed':
      return { label: 'Failed', color: 'red' };
    default:
      return { label: 'Unknown', color: 'gray' };
  }
}

export function getRegressionStatusMeta(status: ReleaseRegressionStatus): {
  label: string;
  color: 'green' | 'red' | 'yellow' | 'gray';
} {
  switch (status) {
    case 'pending':
      return { label: 'Pending', color: 'gray' };
    case 'passed':
      return { label: 'Passed', color: 'green' };
    case 'failed':
      return { label: 'Failed', color: 'red' };
    case 'skipped':
      return { label: 'Skipped', color: 'yellow' };
  }
}

export function getTriggerSourceLabel(source: ReleaseQATriggerSource): string {
  switch (source) {
    case 'manual':
      return 'Manual';
    case 'scheduled':
      return 'Scheduled';
    case 'webhook':
      return 'Webhook';
    case 'api':
      return 'API';
  }
}

export function isActiveStatus(status: ReleaseQARunStatus | undefined): boolean {
  if (!status) return false;
  return [
    'pending',
    'cloning',
    'analyzing',
    'planning',
    'building_workflows',
    'testing',
    'compiling_report',
  ].includes(status);
}

/**
 * Maps a run's status to `TimelineNode`'s dot-color variant when the band has
 * no released-commit suite ring to show instead. Shared by `TimelineRunEntry`
 * and `ReleaseRunPaneHeader`, which echo the same node/status treatment so
 * the detail pane reads as a continuation of the selected band.
 */
export function nodeStatusFor(status: ReleaseQARunStatus): 'success' | 'failed' | 'active' | 'neutral' {
  if (status === 'completed') return 'success';
  if (status === 'failed' || status === 'completed_with_errors') return 'failed';
  if (isActiveStatus(status)) return 'active';
  return 'neutral';
}

/** Shared by `TimelineRunEntry` and `ReleaseRunPaneHeader` — see `nodeStatusFor`. */
export function statusTextClasses(status: ReleaseQARunStatus) {
  if (status === 'completed') return 'text-green-600';
  if (status === 'failed' || status === 'completed_with_errors') return 'text-red-600';
  if (isActiveStatus(status)) return 'text-blue-600';
  return 'text-neutral-500';
}

type ReleaseTestStats = {
  total: number;
  healthy: number;
  unhealthy: number;
  notApplicable: number;
  pending: number;
};

export function computeTestStats(
  todoItems: ReleaseQATodoItem[] | null | undefined
): ReleaseTestStats {
  if (!todoItems || todoItems.length === 0) {
    return { total: 0, healthy: 0, unhealthy: 0, notApplicable: 0, pending: 0 };
  }
  return {
    total: todoItems.length,
    healthy: todoItems.filter((t) => t.status === 'healthy').length,
    unhealthy: todoItems.filter((t) => t.status === 'unhealthy').length,
    notApplicable: todoItems.filter((t) => t.status === 'not_applicable').length,
    pending: todoItems.filter((t) => t.status === 'pending').length,
  };
}

export function formatTestStats(stats: ReleaseTestStats): string {
  if (stats.total === 0) return '-';
  const passed = stats.healthy;
  const total = stats.total - stats.notApplicable;
  if (total === 0) return '-';
  return `${passed}/${total}`;
}

// ============================================================================
// Release QA Configuration Types
// ============================================================================

/** Schedule configuration for simple daily schedules */
export type SimpleScheduleConfig = {
  time: string; // HH:mm format
  timezone: string; // IANA timezone
};

/** Schedule configuration for cron-based schedules */
export type CronScheduleConfig = {
  pattern: string;
  timezone: string;
};

/** Schedule configuration for interval-based schedules */
export type IntervalScheduleConfig = {
  intervalMs: number;
};

/** Combined schedule settings */
export type ScheduleSettings = {
  enabled: boolean;
  scheduleType: 'simple' | 'cron' | 'interval';
  scheduleConfig: SimpleScheduleConfig | CronScheduleConfig | IntervalScheduleConfig;
};

/** Release QA configuration. Repos (and their branches) live in `repos[]`. */
export type ReleaseQAConfig = {
  id: string;
  orgId: string;
  propertyId: string;
  regressionSelectionMode: 'all_published' | 'tag';
  /** @deprecated single-tag legacy field — use regressionTags */
  regressionTag: string | null;
  /** Tags (any-of) used to scope the regression suite. */
  regressionTags: string[] | null;
  /** Environment of the config's property to target when running regressions. */
  regressionEnvironmentId: string | null;
  lookbackDays: number;
  /**
   * Resolved schedule settings (the release schedule automation rule), surfaced
   * by GET so the config dialog can render the real state. `null` when there is
   * no enabled schedule (none configured, or paused).
   */
  schedule?: ScheduleSettings | null;
  /** @deprecated every release is agentic; ignored by the UI */
  agentEnabled?: boolean;
  agentPromptMd: string | null;
  agentMayPromote: boolean;
  createdAt: string;
  updatedAt: string;
};

/** One linked repo on a Release QA config (multi-repo support). */
export type ReleaseQAConfigRepoEntry = {
  linkId: string;
  propertyGithubRepoId: string;
  releaseBranch: string | null;
  role: string | null;
  displayOrder: number;
  repoFullName: string;
  repoOwner: string | null;
  repoName: string | null;
  repoDefaultBranch: string | null;
  repoIsPrivate: boolean | null;
  repoRootDir: string | null;
  repoAgentHint: string | null;
};

/** API response for release QA config */
export type ReleaseQAConfigResponse = {
  ok: boolean;
  config: ReleaseQAConfig | null;
  repos?: ReleaseQAConfigRepoEntry[];
  /** Suites currently linked to the release agent for this config. */
  suites?: ReleaseQAConfigSuiteEntry[];
};

/** A single repo to link on save. */
export type SaveReleaseConfigRepo = {
  propertyGithubRepoId: string;
  releaseBranch?: string | null;
  role?: string | null;
};

/** Input for saving release QA config */
export type SaveReleaseConfigInput = {
  propertyId: string;
  isNew: boolean;
  /**
   * Legacy single-repo path. Either this or `repos[]` must be provided. New
   * dialogs send `repos[]`; older callers can keep using `propertyGithubRepoId`.
   */
  propertyGithubRepoId?: string;
  /** Multi-repo path. Order matters — index 0 is the primary repo. */
  repos?: SaveReleaseConfigRepo[];
  releaseBranch?: string | null;
  regressionEnvironmentId?: string | null;
  lookbackDays?: number;
  schedule?: ScheduleSettings | null;
  /** Release agent (specs/pending/release-agent.md slice 4). */
  agentPromptMd?: string | null;
  agentMayPromote?: boolean;
  /**
   * The regression selector for every config, agent or not — required on
   * create and on every update (`RELEASE_QA_REQUIRES_SUITE` if empty).
   * `regressionSelectionMode`/`regressionTags` stay read-only fields on
   * `ReleaseQAConfig` for legacy configs that haven't been re-saved yet.
   */
  suiteIds: string[];
};

// ============================================================================
// Release Agent Types (specs/pending/release-agent.md slice 4/5)
// ============================================================================

export type ReleaseAgentStatus =
  | 'running'
  | 'sleeping'
  | 'waiting_for_human'
  | 'completed'
  | 'failed'
  | 'canceled';

export type ReleaseAgentChatProvider = 'slack';

/** `durable_agents` row for a release run's agent. */
export type ReleaseAgent = {
  id: string;
  orgId: string;
  kind: string;
  entityId: string;
  conversationId: string;
  jobId: string | null;
  status: ReleaseAgentStatus;
  wakeReason: string | null;
  sleepUntil: string | null;
  chatProviderId: ReleaseAgentChatProvider;
  chatChannelRef: string;
  chatThreadRef: string;
  outcome: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * `GET /release-qa/runs/:id/agent` also returns the agent's open questions
 * and transcript; the web reads only `agent` (its status drives the
 * assistant auto-open — `useReleaseAgentAutoOpen`), so only that is typed.
 */
export type ReleaseAgentResponse = {
  ok: boolean;
  agent: ReleaseAgent;
};

const TERMINAL_AGENT_STATUSES: ReleaseAgentStatus[] = ['completed', 'failed', 'canceled'];

export function isTerminalAgentStatus(status: ReleaseAgentStatus | undefined): boolean {
  if (!status) return false;
  return TERMINAL_AGENT_STATUSES.includes(status);
}

/** Statuses where the agent is actively working or waiting on something — the timeline auto-opens the assistant onto it (`useReleaseAgentAutoOpen`). */
const LIVE_AGENT_STATUSES: ReleaseAgentStatus[] = ['running', 'sleeping', 'waiting_for_human'];

export function isLiveAgentStatus(status: ReleaseAgentStatus | undefined): boolean {
  if (!status) return false;
  return LIVE_AGENT_STATUSES.includes(status);
}

/** Linked GitHub repository */
export type LinkedGitHubRepo = {
  id: string;
  repoFullName: string;
  repoName: string | null;
  repoOwner: string | null;
  isPrivate: boolean | null;
  defaultBranch: string | null;
  linkedAt: string;
  // Monorepo support hints
  rootDir: string | null;
  agentHint: string | null;
};

/** API response for property GitHub repos */
export type PropertyGitHubReposResponse = {
  ok: boolean;
  items: LinkedGitHubRepo[];
};
